import uuid from "uuid-random";
import { Session, Instructor, Classroom, Section } from "@models/ISchema";
import { Week, LinkPageType } from "./linkData.reducer";
import { initialState } from "./EditSessionsContext";
import { intervalsFormat } from "../utils/sessionFormat";
import {
  validateSession,
  handleSavedSessions,
  getNewEditedSessions,
  newSessionsToCreateByFormState,
  savableSessionsToCreate,
  sessionsToCreateBySelectedDays,
  getNewSelectedSessionsBySection,
  forkSelectedSessions as forkSelectedSessionsFromContext,
} from "../utils/context";
import { compareSessions } from "../utils/compareSelected";
import {
  FormPageType,
  EditedSession,
  Assignment,
  ForkSelectedSessionsPayload,
  SelectedSessionsBySectionPayload,
  AllSessionsSelectedPayload,
  ProposalChangesPayload,
  EMPTY_ASSIGNMENT_EDITS,
  EMPTY_RESOURCE_EDITS,
} from "./formData.reducer";

export const addSessionsToCreate = (state: FormPageType, sessionType: "new" | "cloned") => {
  const newSessionsToCreate = newSessionsToCreateByFormState(state, sessionType);
  return {
    ...state,
    sessionsToCreate: [...state.sessionsToCreate, ...newSessionsToCreate],
    selectedSessions: newSessionsToCreate,
    editedSessions: {
      instructors: newSessionsToCreate?.[0]?.instructors,
      blocks: newSessionsToCreate?.[0]?.blocks,
      classrooms: newSessionsToCreate?.[0]?.classrooms,
      intervals: newSessionsToCreate?.[0]?.intervals,
    } as EditedSession,
  };
};

export const addSessionsToCreateBySelectedDays = (link: LinkPageType, state: FormPageType) => {
  const sessionsToCreate = sessionsToCreateBySelectedDays(state);
  const { editedSessions, assignmentSame } = getNewEditedSessions(
    sessionsToCreate,
    sessionsToCreate,
    link,
  );
  return {
    ...state,
    sessionsToCreate: [...(state?.sessionsToCreate ?? []), ...sessionsToCreate],
    selectedCreateSession: false,
    selectedSessions: sessionsToCreate,
    editedSessions,
    assignmentSame,
    errors: [],
  };
};

export const addSessionsToCreateWithNoDays = (state: FormPageType) => {
  const newSession: EditedSession = {
    id: uuid(),
    blocks: state?.editedSessions?.blocks,
    instructors: state?.editedSessions?.instructors as Instructor[],
    classrooms: state?.editedSessions?.classrooms as Classroom[],
    intervals: state?.editedSessions?.intervals as Week[],
    session: null as Session,
    isNew: true,
    isCloned: false,
    section: state?.selectedSection,
  };

  return {
    ...state,
    sessionsToCreate: [...(state?.sessionsToCreate ?? []), newSession],
    selectedCreateSession: false,
    selectedSessions: [newSession],
    errors: [],
  };
};

export const assignmentEdited = (payload: Assignment, state: FormPageType) => {
  return {
    ...state,
    assignmentEdited: {
      ...state?.assignmentEdited,
      ...payload,
    },
  };
};

export const setKeepSessionDuration = (
  payload: { sessionId: string; keepSessionDuration: boolean },
  state: FormPageType,
) => {
  return {
    ...state,
    sessionsToKeepDuration: {
      ...state?.sessionsToKeepDuration,
      [payload?.sessionId]: payload?.keepSessionDuration,
    },
  };
};

export const blocksEditedSessions = ({ blocks }: EditedSession, state: FormPageType) => {
  return {
    ...state,
    editedSessions: {
      ...state.editedSessions,
      blocks,
    },
    resourceEdits: {
      ...state.resourceEdits,
      blocks: true,
    },
  };
};

export const syncBlocks = ({ blocks }: EditedSession, state: FormPageType) => {
  // Update blocks without flagging resourceEdits.blocks. Use for derived/sync
  // updates (e.g., endTime normalization on selection init), not user edits.
  return {
    ...state,
    editedSessions: {
      ...state.editedSessions,
      blocks,
    },
  };
};

export const classroomEditedSessions = ({ classrooms }: EditedSession, state: FormPageType) => {
  return {
    ...state,
    editedSessions: {
      ...state.editedSessions,
      classrooms,
    },
    resourceEdits: {
      ...state.resourceEdits,
      classrooms: true,
    },
  };
};

export const cleanForm = (state: FormPageType) => {
  return {
    ...state,
    ...structuredClone(initialState.form),
  };
};

export const cleanFormCreateSession = (state: FormPageType) => {
  return {
    ...state,
    selectedCreateSession: false,
    selectedSessions: [],
    cloneSelected: [],
    editedSessions: null,
  };
};

export const cleanUnsavableSessionsToCreate = (state: FormPageType) => {
  const sessionsToCreate = savableSessionsToCreate(state.sessionsToCreate);
  return {
    ...state,
    sessionsToCreate,
  };
};

export const forkSelectedSessions = (
  link: LinkPageType,
  state: FormPageType,
  payload: ForkSelectedSessionsPayload,
) => {
  const { newSessions, savedSessions, unmarkedWeekIdsBySessionId } =
    forkSelectedSessionsFromContext(
      state,
      link,
      payload?.sessions ?? [],
      payload?.isLocked ?? (() => false),
    );
  const sessionsToCreate = [
    ...(state?.sessionsToCreate ?? []),
    ...(newSessions ?? []),
  ] as EditedSession[];

  return {
    ...state,
    savedSessions,
    sessionsToCreate,
    selectedSessions: newSessions,
    unmarkedWeekIdsBySessionId,
    // The edition is already materialized on each forked session, so there is
    // no pending multi-edit left to broadcast. Leaving these flags raised would
    // make the next `handleSavedSessions` overwrite every fork with the shared
    // form values, which are emptied as soon as two forks differ.
    assignmentEdited: structuredClone(EMPTY_ASSIGNMENT_EDITS),
    resourceEdits: structuredClone(EMPTY_RESOURCE_EDITS),
    ...compareSessions(newSessions, link?.weeks ?? []),
  };
};

export const formCancel = (
  payload: (Session | EditedSession)[],
  link: LinkPageType,
  state: FormPageType,
) => {
  const cancel = getNewEditedSessions(state?.savedSessions, payload, link);
  return {
    ...state,
    ...cancel,
    assignmentEdited: cancel.assignmentSame,
    resourceEdits: structuredClone(EMPTY_RESOURCE_EDITS),
    errors: [],
  };
};

/**
 * @param ErrorForm[]
 * @param state FormPageType
 *
 * @return state FormPageType
 */
export const formError = (errors: FormPageType["errors"], state: FormPageType): FormPageType => ({
  ...state,
  errors,
});

/**
 * Loads the pending changes of an existing proposal (edited, created and deleted sessions) in a
 * single step, with nothing selected, so the editor never renders a half-applied proposal.
 */
export const loadProposalChanges = (
  payload: ProposalChangesPayload,
  state: FormPageType,
): FormPageType => ({
  ...state,
  savedSessions: payload?.savedSessions ?? [],
  sessionsToCreate: payload?.sessionsToCreate ?? [],
  savedSessionsToCreateIds: payload?.savedSessionsToCreateIds ?? {},
  sessionsToDelete: payload?.sessionsToDelete ?? {},
  selectedSessions: [],
  cloneSelected: [],
  editedSessions: null,
  selectedCreateSession: false,
  selectedSection: null,
  removedWeekIdsBySessionId: {},
  unmarkedWeekIdsBySessionId: {},
  resourceEdits: structuredClone(EMPTY_RESOURCE_EDITS),
  errors: [],
});

/**
 * @param EditedSession
 * @param state FormPageType
 *
 * @return state FormPageType
 */
export const instructorEditedSessions = ({ instructors }: EditedSession, state: FormPageType) => {
  return {
    ...state,
    editedSessions: {
      ...state.editedSessions,
      instructors,
    },
    resourceEdits: {
      ...state.resourceEdits,
      instructors: true,
    },
  };
};

/**
 * @param EditedSession
 * @param state FormPageType
 *
 * @return state FormPageType
 */
export const intervalsEditedSessions = (
  {
    intervals,
    weeks,
    originalWeeksBySessionId = {},
  }: EditedSession & {
    weeks?: { [weekId: string | number]: Week };
    originalWeeksBySessionId?: { [key: string]: string[] };
  },
  state: FormPageType,
) => {
  const selectedSessions = state.selectedSessions;
  const unmarkedWeekIdsBySessionId = Object.fromEntries(
    Object.entries(state?.unmarkedWeekIdsBySessionId ?? {}).map(([sessionId, weekIds]) => [
      sessionId,
      { ...weekIds },
    ]),
  );

  if (!!Object.keys(weeks ?? {}).length) {
    selectedSessions.forEach(session => {
      unmarkedWeekIdsBySessionId[session?.id] = {
        ...(unmarkedWeekIdsBySessionId?.[session?.id] ?? {}),
        ...weeks,
      };

      Object.keys(unmarkedWeekIdsBySessionId?.[session?.id] ?? {}).forEach(weekId => {
        const week = weeks?.[weekId];
        const originalWeeks = originalWeeksBySessionId[session?.id] ?? [];

        if (week?.checked || !originalWeeks.includes(weekId)) {
          delete unmarkedWeekIdsBySessionId[session?.id][weekId];
        }
      });
    });
  }

  return {
    ...state,
    editedSessions: {
      ...state.editedSessions,
      intervals,
    },
    unmarkedWeekIdsBySessionId,
  };
};

/**
 * @param state FormPageType
 *
 * @return state FormPageType
 */
export const notSelectedCreateSession = (state: FormPageType): FormPageType => ({
  ...state,
  selectedCreateSession: false,
});

export const removeSelectedSessions = (
  selectedSessions: (Session | EditedSession)[],
  state: FormPageType,
) => {
  const selectedSessionsById = (selectedSessions ?? []).reduce<
    Record<string, Session | EditedSession>
  >((table, session) => {
    table[session?.id] = session;
    return table;
  }, {});
  const isNotSelected = (session: Session | EditedSession) =>
    !Object.prototype.hasOwnProperty.call(selectedSessionsById, session?.id ?? "-");
  const filteredSavedSessions = (state?.savedSessions ?? []).filter(isNotSelected);
  const filteredSessionsToCreate = (state?.sessionsToCreate ?? []).filter(isNotSelected);
  const newSessionsToDelete = selectedSessions
    .filter(({ id }) => !id?.includes("-"))
    .reduce<Record<string, Session | EditedSession>>((acc, session) => {
      acc[session?.id] = session;
      return acc;
    }, {});

  return {
    ...state,
    editedSessions: {
      blocks: { day: null, selected: null },
      classrooms: null,
      instructors: null,
      intervals: null,
    },
    savedSessions: filteredSavedSessions,
    selectedSessions: [],
    selectedSection: null,
    cloneSelected: [],
    sessionsToCreate: filteredSessionsToCreate,
    sessionsToDelete: { ...(state?.sessionsToDelete ?? {}), ...newSessionsToDelete },
    resourceEdits: structuredClone(EMPTY_RESOURCE_EDITS),
  };
};

export const savedNewSession = (payload: EditedSession, state: FormPageType) => {
  let savedNew = state.savedSessions ? [...state.savedSessions] : [];
  const sessionNew = { ...payload };
  if (sessionNew.id) {
    const findSession = savedNew.find(value => value.id === sessionNew.id);
    if (findSession) {
      // update new session
      const rejectSession = savedNew.filter(session => session.id !== findSession.id);
      savedNew = rejectSession;
      savedNew.push(sessionNew);
    } else {
      // create new session without id
      sessionNew.isNew = true;
      sessionNew.section = state?.selectedSection;
      savedNew.push(sessionNew);
    }
  } else {
    // create new session with id
    sessionNew.id = uuid();
    sessionNew.isNew = true;
    sessionNew.section = state?.selectedSection;
    savedNew.push(sessionNew);
  }
  return {
    ...state,
    savedSessions: savedNew,
    selectedCreateSession: true,
    selectedSessions: [],
    cloneSelected: [],
    editedSessions: payload,
  };
};

export const savedSessions = (link: LinkPageType, state: FormPageType) => {
  const { saveValidation, naturalSessions, sessionsToCreate, savedSessionsToCreateIds } =
    handleSavedSessions(state, link);

  return {
    ...state,
    savedSessions: naturalSessions,
    sessionsToCreate,
    savedSessionsToCreateIds: {
      ...state.savedSessionsToCreateIds,
      ...savedSessionsToCreateIds,
    },
    ...saveValidation,
    errors: [],
  };
};

export const selectedCreateSession = (
  payload: Section,
  link: LinkPageType,
  state: FormPageType,
) => {
  // check by default the weeks that are related to the link's course-component
  const component = payload?.component?.code;
  const assignedWeeks = link?.assignedWeeksByComponent?.[component] ?? {};

  const intervalsWithoutAssignedWeeks = [];
  const intervalsWithAssignedWeeks = [];
  let hasSomeWeekOutsideTerm = false;

  intervalsFormat(null, link?.weeks).forEach(week => {
    if (!week?.isInTerm) {
      hasSomeWeekOutsideTerm = true;
    }

    intervalsWithoutAssignedWeeks.push({
      ...week,
      checked: false,
    });

    intervalsWithAssignedWeeks.push({
      ...week,
      checked: Object.prototype.hasOwnProperty.call(assignedWeeks, week?.id),
    });
  });

  return {
    ...state,
    selectedSessions: [],
    cloneSelected: [],
    editedSessions: {
      blocks: {
        days: [],
        day: null,
        startTime: null,
        endTime: null,
        blocks: null,
        selected: null,
      },
      instructors: [],
      classrooms: [],
      intervals: hasSomeWeekOutsideTerm
        ? intervalsWithoutAssignedWeeks
        : intervalsWithAssignedWeeks,
    },
    selectedCreateSession: true,
    selectedSection: payload,
  };
};

export const selectedSaveSession = (payload: EditedSession, state: FormPageType) => {
  return {
    ...state,
    editedSessions: payload,
    selectedSessions: [],
    errors: [],
    selectedSection: payload.section,
  };
};

export const selectedSessions = (
  payload: Session | EditedSession,
  link: LinkPageType,
  state: FormPageType,
) => {
  const { assignmentSame, cloneSelected, editedSessions } = validateSession(payload, state, link);

  return {
    ...state,
    selectedSessions: cloneSelected,
    assignmentEdited: assignmentSame,
    editedSessions,
    resourceEdits: structuredClone(EMPTY_RESOURCE_EDITS),
    errors: [],
  };
};

export const setAllSessionsSelected = (
  payload: AllSessionsSelectedPayload,
  link: LinkPageType,
  state: FormPageType,
) => {
  if (!payload.checked) {
    return {
      ...state,
      selectedSessions: [],
      assignmentSame: {
        blocks: true,
        day: true,
        instructors: true,
        classrooms: true,
        intervals: true,
      },
      resourceEdits: structuredClone(EMPTY_RESOURCE_EDITS),
      errors: [],
      selectedCreateSession: false,
    };
  }

  const hiddenSessionIds = new Set(payload.hiddenSessionIds ?? []);

  const newSelectedSessionsBySection = payload.sections
    .map(section => [...(section.sessions ?? []), ...(section.unasignedSessions ?? [])])
    .flat()
    .filter(session => !hiddenSessionIds.has(session.id));

  const validationBySection = getNewEditedSessions(
    state?.savedSessions ?? [],
    newSelectedSessionsBySection,
    link,
  );

  return {
    ...state,
    ...validationBySection,
    selectedSessions: newSelectedSessionsBySection,
    assignmentEdited: validationBySection.assignmentSame,
    resourceEdits: structuredClone(EMPTY_RESOURCE_EDITS),
    errors: [],
    selectedCreateSession: false,
  };
};

export const selectedSessionsBySection = (
  payload: SelectedSessionsBySectionPayload,
  link: LinkPageType,
  state: FormPageType,
) => {
  const newSelectedSessionsBySection = getNewSelectedSessionsBySection(
    payload.checked,
    payload.sessionsOfSection,
    state,
    payload.sectionId,
  );
  const validationBySection = getNewEditedSessions(
    state?.savedSessions ?? [],
    newSelectedSessionsBySection,
    link,
  );
  return {
    ...state,
    ...validationBySection,
    selectedSessions: newSelectedSessionsBySection,
    assignmentEdited: validationBySection.assignmentSame,
    resourceEdits: structuredClone(EMPTY_RESOURCE_EDITS),
    errors: [],
    selectedCreateSession: false,
  };
};

export const setRemovedWeekIdsBySessionId = (
  payload: { [key: string]: string[] },
  state: FormPageType,
) => {
  return {
    ...state,
    removedWeekIdsBySessionId: Object.fromEntries(
      Object.entries({ ...(state?.removedWeekIdsBySessionId ?? {}), ...payload }).filter(
        ([, weekIds]) => (weekIds as string[])?.length > 0,
      ),
    ),
  };
};

/**
 * @param boolean
 * @param state FormPageType
 *
 * @return state FormPageType
 */
export const setSessionFromLocationSelected = (
  value: FormPageType["sessionFromLocationSelected"],
  state: FormPageType,
): FormPageType => ({ ...state, sessionFromLocationSelected: value });

/**
 * @param string
 * @param state FormPageType
 *
 * @return state FormPageType
 */
export const setSessionRecommendationIdSelected = (
  value: FormPageType["sessionRecommendationIdSelected"],
  state: FormPageType,
): FormPageType => ({ ...state, sessionRecommendationIdSelected: value });

export const undoEditionOverSelectedSessions = (payload: EditedSession[], state: FormPageType) => {
  const selected = payload;
  const selectedById = selected.reduce<Record<string, boolean>>((acc, session) => {
    acc[session?.id] = true;
    return acc;
  }, {});
  const nonSelected = (sessions: EditedSession[] | null | undefined) =>
    (sessions ?? []).filter(
      session => !Object.prototype.hasOwnProperty.call(selectedById, session?.id ?? "-"),
    );

  const unmarkedWeekIdsBySessionId = Object.entries(state?.unmarkedWeekIdsBySessionId ?? {}).reduce(
    (acc, [sessionId, weekIds]) => {
      if (!selectedById[sessionId]) {
        acc[sessionId] = weekIds;
      }

      return acc;
    },
    {},
  );

  const savedSessionsToCreateIds = Object.entries(
    (state?.savedSessionsToCreateIds ?? {}) as Record<string, boolean>,
  ).reduce((acc, [sessionId, isSelected]) => {
    if (!selectedById[sessionId]) {
      acc[sessionId] = isSelected;
    }

    return acc;
  }, {});

  return {
    ...state,
    editedSessions: selected?.length ? state?.editedSessions : null,
    savedSessions: nonSelected(state?.savedSessions),
    selectedSessions: [],
    sessionsToCreate: nonSelected(state?.sessionsToCreate),
    cloneSelected: nonSelected(state?.cloneSelected),
    sessionsToDelete: Object.fromEntries(
      Object.entries(state?.sessionsToDelete ?? {}).filter(
        ([sessionId]) => !Object.prototype.hasOwnProperty.call(selectedById, sessionId),
      ),
    ),
    unmarkedWeekIdsBySessionId,
    savedSessionsToCreateIds,
    removedWeekIdsBySessionId: Object.fromEntries(
      Object.entries(state?.removedWeekIdsBySessionId ?? {}).filter(
        ([sessionId]) => !Object.prototype.hasOwnProperty.call(selectedById, sessionId),
      ),
    ),
    resourceEdits: structuredClone(EMPTY_RESOURCE_EDITS),
  };
};

export const setBlockingThreshold = (payload: string | null, state: FormPageType) => {
  return {
    ...state,
    blockingThreshold: payload,
  };
};
